import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type { AdminMessageSummary } from '../types/notificationMessage';

const BASE = '/admin/v1/notification/messages';

interface Envelope<T> {
  success: boolean;
  data?: T | null;
  error?: { code?: string; message?: string } | null;
}

const isConflictError = (status: number, code: string | undefined): boolean =>
  status === 409 || code === 'COMMON_CONFLICT';

const isNotFoundError = (status: number, code: string | undefined): boolean =>
  status === 404 || code === 'COMMON_NOT_FOUND';

const isForbiddenError = (status: number, code: string | undefined): boolean =>
  status === 403 || code === 'COMMON_FORBIDDEN';

class ResendApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(status: number, code: string | undefined, message: string) {
    super(message);
    this.name = 'ResendApiError';
    this.status = status;
    this.code = code;
  }

  isConflict(): boolean {
    return isConflictError(this.status, this.code);
  }

  isNotFound(): boolean {
    return isNotFoundError(this.status, this.code);
  }

  isForbidden(): boolean {
    return isForbiddenError(this.status, this.code);
  }
}

export { ResendApiError };

const readEnvelope = async <T,>(res: Response, fallbackMsg: string): Promise<T> => {
  let env: Envelope<T> | null = null;
  try {
    env = (await res.json()) as Envelope<T>;
  } catch {
    env = null;
  }
  if (!res.ok || !env || env.success !== true) {
    const code = env?.error?.code;
    const msg = env?.error?.message ?? fallbackMsg;
    throw new ResendApiError(res.status, code, msg);
  }
  if (env.data == null) {
    throw new ResendApiError(res.status, env.error?.code, fallbackMsg);
  }
  return env.data;
};

/**
 * 발송 실패 메시지 재발송 (BUG-016).
 *
 * `POST /admin/v1/notification/messages/{messageId}/resend` — body 없음,
 * Idempotency-Key 불필요. 성공 시 메시지 상태가 QUEUED 로 전환된다.
 * 재발송 가능 상태: FAILED_PERMANENT / FAILED_TRANSIENT.
 * 오류: 409 (재발송 불가 상태), 400 (메시지 없음), 403 (권한 없음).
 */
export const useResendMessageMutation = (): UseMutationResult<
  AdminMessageSummary,
  ResendApiError,
  string
> => {
  const qc = useQueryClient();
  return useMutation<AdminMessageSummary, ResendApiError, string>({
    mutationFn: async (messageId) => {
      const res = await adminFetch(`${BASE}/${messageId}/resend`, {
        method: 'POST',
      });
      return readEnvelope<AdminMessageSummary>(res, '재발송에 실패했습니다');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'notification-messages'] });
    },
  });
};
